import { getAppConfig } from '@/utils/env';
import { defineStore, storeToRefs, useRelationStore, useSelectionStore, useStyle3dStore } from '@repo/stores';
import { ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { until } from '@vueuse/core';
import {
  type DPM,
  type FabricShareInfo,
  mergeShareFabricInfo,
  type Relation,
  type Shape,
  useStyle3d
} from '@repo/style3d';
import axios, { type AxiosResponse } from 'axios';
import { delay, joinUrl } from '@repo/utils';
import { getStyle3dFabricApi } from '@/api';
import { useLoading } from '@repo/components';
import { useRecentProjectListStore } from '@/store/recent-project-list.ts';
import { useFabricStore } from '@/store/fabric.ts';
import { generateFabricInfo, getEnterpriseIdFromFabric, getFabricShareInfo } from '@/utils';
import { getEnterpriseById } from '@/api/enterprise';

const { OSS_BASE_URL, INDIVIDUAL_SHARES, INDIVIDUAL_FABRIC_SHARES } = getAppConfig();
const projectRootPath = `${OSS_BASE_URL}${INDIVIDUAL_SHARES}`;
const fabricRootPath = `${OSS_BASE_URL}${INDIVIDUAL_FABRIC_SHARES}`;

const need3DResourceRoutes = ['project', 'fabric', 'style3d', 'detail'];

interface BasicResource {
  id: string;
  originUrl?: string;
  color?: {
    colorNo?: string,
    color: string
  };
  // 支持多个materialIndex的颜色，key为materialIndex，value为颜色信息
  colors?: Record<number, { colorNo?: string, color: string }>;
  name?: string
}


interface CloudknitScoResource extends BasicResource {
  type: 'cloudknit';
  url?: string;
}

interface Style3dScoResource extends BasicResource {
  type: 'style3d';
  url: string;
}

type ScoResource = CloudknitScoResource | Style3dScoResource;


interface CloudknitFabricResource extends BasicResource {
  type: 'cloudknit';
  // 支持多个面料ID，按materialIndex索引
  fabricIds?: string[];
}

interface Style3dFabricResource extends BasicResource {
  type: 'style3d';
  // 支持多个面料ID，按materialIndex索引
  fabricIds?: string[];
}

type FabricResource = CloudknitFabricResource | Style3dFabricResource;

interface ResourceParam {
  scoResource: ScoResource;
  fabricResource: FabricResource;
  show3d: boolean;
}

function removeProtocol(url: string): string {
  return url.replace(/^https?:/, '');
}

export const useParamStore = defineStore('param', () => {
  const route = useRoute();
  const loadingSpin = useLoading();

  const recentUpdate = ref<number>();

  const resourceParam = ref<ResourceParam>({
    scoResource: {
      type: 'cloudknit',
      id: 'fabric_hanger',
      originUrl: 'https://share.cloudknit.cn/fabric_hanger'
    },
    fabricResource: {
      type: 'cloudknit',
      id: 'default',
      originUrl: 'https://oss.cloudknit.cn/fabric/default'
    },
    show3d: false
  });

  const dispalyname = ref<string>();

  const patternTypeMap = ref<Map<string, string>>(new Map<string, string>());

  const { setModel, loadSco, loadFabric, resetCameraViewState, getScreenshot, getStyle3dSDKInstance } = useStyle3d();

  const style3dStore = useStyle3dStore();
  const { loaded: sdkLoaded, ready: sdkReady, resolution } = storeToRefs(style3dStore);

  const relationStore = useRelationStore();

  async function loadScoResource() {
    console.time('loadScoResource');
    await until(sdkLoaded).toBe(true);

    const scoResource = resourceParam.value.scoResource;
    if (scoResource.type === 'cloudknit') {
      const jsonResult: AxiosResponse<Relation> = await axios.get(joinUrl(projectRootPath, scoResource.id, 'relation.json'));
      const model = jsonResult.data.metadata?.modelType || 'sco';
      const name = jsonResult.data.metadata?.property[0]?.data.find(item => item.key === 'styleName')?.value || '';
      setModel(model.toUpperCase() as 'GLB' | 'SCO');

      let url;
      switch (model) {
        case 'glb':
          url = joinUrl(projectRootPath, scoResource.id, '/glb/emu.glb');
          break;
        default:
          url = joinUrl(projectRootPath, scoResource.id, '/sco/sco.json');

          await axios.get(decodeURIComponent(url)).then((response) => {
            const data = response.data;

            patternTypeMap.value.clear();
            Object.values(data.material).map((item: any) => {
              patternTypeMap.value.set(item.id, item.type);
            });
          });

          break;
      }

      scoResource.url = url;
      scoResource.name = name;
    }
    else{
      let model : string = 'SCO';
      type ScoJson = {
        metadata?: {
          _fileName?: string;
          _typeName?: string;
        };
      };
      try {
        const jsonResult: AxiosResponse<ScoJson> = await axios.get(scoResource.url);
        scoResource.name = jsonResult?.data.metadata?._fileName ?? '';
        model = jsonResult?.data.metadata?._typeName ?? 'SCO';
      } catch (error) {
        console.error("请求 SCO 资源失败：", error);
        scoResource.name = '';
      }
      setModel(model.toUpperCase() as 'GLB' | 'SCO');
    }

    if (!scoResource.url) {
      console.error('scoResource.url is empty');
      return;
    }
    await loadSco(decodeURIComponent(scoResource.url));

    console.timeEnd('loadScoResource');
  }

  const fabricStore = useFabricStore();

  async function loadFabricResource() {
    console.time('loadFabricResource');
    let shapeList: Shape[] = [];

    const show3d = resourceParam.value.show3d;
    const scoResource = resourceParam.value.scoResource;
    const fabricResource = resourceParam.value.fabricResource;

    const fabricRoot = joinUrl(fabricRootPath, fabricResource.id);
    const infoResult: AxiosResponse<FabricShareInfo> = await axios.get(joinUrl(fabricRoot, 'info.json'));
    const fabricShareInfo = infoResult.data;

    if (show3d) {
      await until(sdkReady).toBe(true);
      shapeList = await handle3DResource(scoResource, {
        fabricRootPath: fabricRoot,
        fabricInfo: fabricShareInfo,
        fabricResource
      });

      if (scoResource.type === "style3d"){
        shapeList = await loadFabric(shapeList, {
          fabricRootPath: fabricRoot,
          resolution: resolution.value,
          color: fabricResource.color?.color
        });
      }
      else {
        const maxMaterialIndex = Math.max(...shapeList.map(item => item.materialIndex ?? 0));

        shapeList = await loadFabric(shapeList, {
          fabricRootPath: fabricRoot,
          resolution: resolution.value,
          color: fabricResource.colors?.[0]?.color || fabricResource.color?.color
        }, {getShapeOnly: true});

        const loadedShapes = await loadFabric(shapeList, {
          fabricRootPath: fabricRoot,
          resolution: resolution.value,
          color: fabricResource.colors?.[0]?.color || fabricResource.color?.color
        }, {materialIndex: 0});

        // 更新shapeList中对应的shapes
        loadedShapes.forEach(loadedShape => {
          const index = shapeList.findIndex(s => s.id === loadedShape.id);
          if (index !== -1) {
            shapeList[index] = loadedShape;
          }
        });
        
        // 如果有多个面料ID，加载其他materialIndex的面料
        if (maxMaterialIndex > 0) {
          console.log('[param] 加载多个面料:', { fabricIds: fabricResource.fabricIds });
          
          for (let materialIndex = 1; materialIndex <= maxMaterialIndex; materialIndex++) {
            const fabricId = fabricResource.fabricIds?.[materialIndex] ?? fabricResource.id;
            const fabricRoot = joinUrl(fabricRootPath, fabricId);
            
            console.log(`[param] 加载面料 materialIndex=${materialIndex}, fabricId=${fabricId}`);
            
            try {
              const infoResult: AxiosResponse<FabricShareInfo> = await axios.get(joinUrl(fabricRoot, 'info.json'));
              const fabricShareInfo = infoResult.data;
              
              // 只加载对应materialIndex的shapes
              const shapesForMaterial = shapeList.filter(shape => shape.materialIndex === materialIndex);
              if (shapesForMaterial.length > 0) {
                // 使用mergeShareFabricInfo来正确合并面料信息
                const projectRoot = joinUrl(projectRootPath, scoResource.id);
                const mergedShapes = await mergeShareFabricInfo(
                  shapesForMaterial, 
                  projectRoot, 
                  fabricShareInfo, 
                  {
                    type: 'cloudknit',
                    id: fabricId,
                    originUrl: fabricResource.originUrl,
                    fabricRootPath: fabricRoot
                  }
                );
                
                const loadedShapes = await loadFabric(mergedShapes, {
                  fabricRootPath: fabricRoot,
                  resolution: resolution.value,
                  color: fabricResource.colors?.[materialIndex]?.color || fabricResource.color?.color
                }, {materialIndex: materialIndex});
                
                // 更新shapeList中对应的shapes
                loadedShapes.forEach(loadedShape => {
                  const index = shapeList.findIndex(s => s.id === loadedShape.id);
                  if (index !== -1) {
                    shapeList[index] = loadedShape;
                  }
                });
              }
            } catch (error) {
              console.error(`加载面料${materialIndex} (ID: ${fabricId})失败:`, error);
            }
          }
        }
      }
    } else {
      shapeList = await handle3DResource(scoResource, { fabricInfo: fabricShareInfo, fabricResource });
    }

    relationStore.setShape(shapeList);

    await reloadFabricInfo();
    console.timeEnd('loadFabricResource');
  }

  async function loadStyle3dResource() {
    const show3d = resourceParam.value.show3d;
    const scoResource = resourceParam.value.scoResource;
    const fabricResource = resourceParam.value.fabricResource;

    const style3dFabricInfo = await reloadStyle3dFabricInfo();
    if (!show3d) {
      return;
    }

    const generateRibInfo = scoResource.type !== 'style3d';
    const fabricInfo = await generateFabricInfo(style3dFabricInfo, generateRibInfo);
    await until(sdkReady).toBe(true);
    let shapeList = await handle3DResource(scoResource, { fabricInfo, fabricResource });
    shapeList = await loadFabric(shapeList, {
      fabricRootPath: '',
      resolution: resolution.value,
      color: fabricResource.color?.color
    });
    relationStore.setShape(shapeList);
  }

  async function handle3DResource(
    scoResource: ScoResource,
    fabric: {
      fabricRootPath?: string,
      fabricInfo: Pick<FabricShareInfo, 'info'>,
      fabricResource: FabricResource
    }): Promise<Shape[]> {
    let shapeList: Shape[] = [];

    const { fabricInfo, fabricResource, fabricRootPath } = fabric;
    if (scoResource.type === 'style3d') {
      shapeList = await mapStyle3dMaterialList(fabricInfo, fabricResource);
    } else {
      const projectRoot = joinUrl(projectRootPath, scoResource.id);
      const jsonResult: AxiosResponse<Relation> = await axios.get(joinUrl(projectRoot, 'relation.json'));
      const relation = jsonResult.data;
      relationStore.setRelation(relation);
      if (relation.shape.length > 0) {
        // 只为materialIndex=0的shape合并第一个面料信息
        const shapes0 = relation.shape.filter(s => (s.materialIndex ?? 0) === 0);
        const shapesOther = relation.shape.filter(s => (s.materialIndex ?? 0) !== 0);
        
        const mergedShapes0 = await mergeShareFabricInfo(shapes0, projectRoot, fabricInfo, {
          ...fabricResource,
          fabricRootPath
        });
        
        // 其他materialIndex的shape保持原样，等待后续加载
        shapeList = [...mergedShapes0, ...shapesOther];
      } else {
        shapeList = await mapStyle3dMaterialList(fabricInfo, fabricResource);
      }
    }
    return shapeList;
  }

  async function mapStyle3dMaterialList(
    fabricInfo: Pick<FabricShareInfo, 'info'>,
    fabric?: {
      type: string,
      id: string,
      originUrl?: string
    }) {
    if (!fabricInfo.info.main) {
      console.error('fabricInfo.info.main is empty');
      return [];
    }
    const { dpm, width, height, name } = fabricInfo.info.main;

    const style3dSDK = getStyle3dSDKInstance();
    let shapeList = await style3dSDK!.getMaterialList();
    shapeList = filterAndMapShapes(shapeList, name, width, height, dpm).map(item => ({ ...item, fabric }));
    return shapeList;
  }

  function filterAndMapShapes(
    shapeList: Shape[],
    name: string,
    width?: number,
    height?: number,
    dpm?: DPM) {
    return shapeList
      .filter(item => ['pattern', 'fabric', ''].includes(patternTypeMap.value.get(`${item.id}`) || ''))
      .map(item => ({
        ...item,
        'piece': item.name,
        width,
        height,
        dpm,
        name
      }));
  }

  const selectionStore = useSelectionStore();

  async function reload() {
    loadingSpin.setLoading(true);
    console.time('style3d onReady');

    const show3d = resourceParam.value.show3d;
    const fabricResource = resourceParam.value.fabricResource;

    show3d && await loadScoResource();
    if (fabricResource.type === 'style3d') {
      await loadStyle3dResource();
    } else {
      await loadFabricResource();
    }
    selectionStore.setSelectedFabric('');
    loadingSpin.setLoading(false);

    if (show3d) {
      await delay(100);
      await resetCameraViewState();
    }

    recentUpdate.value = Date.now();
  }

  watch(() => route.name, async () => {
    if (need3DResourceRoutes.includes(route.name as string)) {
      await refreshLoadResourceByRoute();
    }
  }, {
    immediate: true
  });

  async function refreshLoadResourceByRoute() {
    const { type, id, id1, id2 } = route.params as { [key: string]: string };
    const { scoId, sco, originUrl, originScoUrl, color, colors } = route.query as { [key: string]: string };

    console.log('[param] refreshLoadResourceByRoute:', {
      params: { type, id, id1, id2 },
      query: { color, colors }
    });

    const show3d = route.name === 'fabric';
    
    // 处理多面料情况
    let fabricIds: string[] | undefined;
    let mainFabricId: string = id as string;
    
    // 检查路由参数中的多面料
    if (id1 || id2) {
      // fabricIds[0] 对应 materialIndex=0 (主面料)
      // fabricIds[1] 对应 materialIndex=1 (第二个面料)
      // fabricIds[2] 对应 materialIndex=2 (第三个面料)
      fabricIds = [mainFabricId];
      if (id1) fabricIds.push(id1);
      if (id2) fabricIds.push(id2);
      
      console.log('[param] 多面料模式:', { 
        fabricIds,
        mapping: fabricIds.map((id, index) => `materialIndex=${index} -> ${id}`)
      });
    }
    
    // 解析颜色参数：优先使用colors（多颜色），其次使用color（单颜色，向后兼容）
    let parsedColors: Record<number, { colorNo?: string, color: string }> | undefined;
    let parsedColor: { colorNo?: string, color: string } | undefined;
    
    if (colors) {
      try {
        parsedColors = JSON.parse(decodeURIComponent(colors));
        console.log('[param] 解析多颜色参数:', parsedColors);
      } catch (e) {
        console.error('[param] 解析colors参数失败:', e);
      }
    } else if (color) {
      try {
        parsedColor = JSON.parse(decodeURIComponent(color));
        console.log('[param] 解析单颜色参数:', parsedColor);
      } catch (e) {
        console.error('[param] 解析color参数失败:', e);
      }
    }
    
    const fabricResource: FabricResource = {
      type: type === 'style3d' ? 'style3d' : 'cloudknit',
      id: mainFabricId,
      fabricIds,
      originUrl: atob(originUrl || ''),
      color: parsedColor,
      colors: parsedColors
    };

    let defaultScoId = 'fabric_hanger';
    if (fabricResource.type === 'cloudknit') {
      const enterpriseId = await getEnterpriseIdFromFabric(fabricResource.id);
      if (enterpriseId) {
        const enterpriseInfo = await getEnterpriseById(enterpriseId);
        if (enterpriseInfo.webConfig?.scoID) {
          defaultScoId = enterpriseInfo.webConfig.scoID;
        }
        if (enterpriseInfo.webConfig?.dispalyname) {
          dispalyname.value = enterpriseInfo.webConfig.dispalyname;
        }
      }
    }

    const scoPath = atob(sco || '');
    const scoResource: ScoResource = {
      type: scoPath ? 'style3d' : 'cloudknit',
      id: scoId || defaultScoId,
      url: scoPath ? removeProtocol(scoPath) : '',
      originUrl: atob(originScoUrl || '')
    };

    resourceParam.value = {
      scoResource,
      fabricResource,
      show3d
    };

    await reload();
    if (show3d) {
      (scoId || scoPath) && await addToRecentList();
    }
  }

  async function reloadFabricInfo() {
    const fabricId = resourceParam.value.fabricResource.id;
    const originUrl = resourceParam.value.fabricResource.originUrl;

    const fabricInfo = await getFabricShareInfo(fabricId, originUrl);
    await fabricStore.setFabricInfo(fabricInfo);

    return fabricInfo;
  }

  async function reloadStyle3dFabricInfo() {
    const style3dFabricId = resourceParam.value.fabricResource.id;
    const originUrl = resourceParam.value.fabricResource.originUrl;

    const style3dFabric = await getStyle3dFabricApi(style3dFabricId);
    const xhr_path = style3dFabric.data.xhr_path;
    const thumb = style3dFabric.data.thumb_path;

    const keyMap = style3dFabric.create_info.fields.map(item => ({ name: item.name, key: item.key }));
    const fabricProperty = keyMap
      .map((item) => ({ name: item.name, key: item.key, value: style3dFabric.data[item.key] }))
      .filter(item => item.value);

    const carousels = style3dFabric.data?.all_thumbs?.map((item: any) => {
      const url = item.thumb_path.split('?')[0];
      const urlParts = url.split('/');
      const decodedFileName = decodeURIComponent(urlParts[urlParts.length - 1]);
      const fileType = decodedFileName?.split('.')?.pop()?.toLowerCase() || '';

      const fileCategory = ['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(fileType) ? 'picture' :
        ['mp4', 'avi', 'mov', 'wmv'].includes(fileType) ? 'video' : 'unknown';

      return {
        name: decodedFileName,
        type: fileCategory,
        url
      };
    });

    const fabricInfo = {
      id: `style3d-${style3dFabricId}`,
      originUrl,
      fabricId: `${style3dFabricId}`,
      info: {
        width: style3dFabric.data.width,
        height: style3dFabric.data.height
      },
      carousels,
      resourceType: 'style3d' as const,
      enterpriseId: '',
      enterpriseName: '',
      thumb,
      xhr_path,
      properties: fabricProperty
    };
    await fabricStore.setFabricInfo(fabricInfo);

    return fabricInfo;
  }

  const recentProjectListStore = useRecentProjectListStore();

  async function addToRecentList() {
    const thumb = await getScreenshot();

    const scoResource = resourceParam.value.scoResource;
    const id = scoResource.type === 'style3d' ? `style3d-${scoResource.id}` : scoResource.id;
    if (scoResource.type === 'style3d') {
      await recentProjectListStore.addRecentProject({
        id,
        scoId: scoResource.id,
        scoPath: scoResource.url,
        type: scoResource.type,
        originUrl: scoResource.originUrl,
        name: scoResource.name,
        thumb
      });
    } else {
      await recentProjectListStore.addRecentProject({
        id,
        scoId: scoResource.id,
        type: scoResource.type,
        originUrl: scoResource.originUrl,
        name: scoResource.name,
        thumb
      });
    }
  }

  return { dispalyname, recentUpdate, resourceParam, patternTypeMap, refreshLoadResourceByRoute };
});
